import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "@/utils/numberFormatter";
import { Truck } from "lucide-react";

interface TransportLeg {
  vehicleType: string;
  distance: number;
}

interface TransportLegsCardProps {
  transports?: TransportLeg[];
  className?: string;
}

/**
 * Per-leg breakdown of the quarry → transformation transport for a product
 * (e.g. import via air + land). Renders nothing when there are no valid legs.
 */
export const TransportLegsCard = ({ transports, className }: TransportLegsCardProps) => {
  const { t } = useLanguage();

  const legs = (transports || []).filter((leg) => leg && leg.vehicleType && (Number(leg.distance) || 0) > 0);
  if (legs.length === 0) return null;

  const totalDistance = legs.reduce((sum, leg) => sum + (Number(leg.distance) || 0), 0);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-orange-600" />
          {t("results.transport.title")}
        </CardTitle>
        <p className="text-sm text-gray-600">{t("results.transport.description")}</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="p-2 text-left text-sm font-medium text-gray-700">#</th>
                <th className="p-2 text-left text-sm font-medium text-gray-700">{t("products.vehicleType")}</th>
                <th className="p-2 text-right text-sm font-medium text-gray-700">{t("products.distanceTraveled")}</th>
              </tr>
            </thead>
            <tbody>
              {legs.map((leg, index) => (
                <tr key={index} className="border-b border-gray-100 last:border-0">
                  <td className="p-2 text-sm text-gray-500">{index + 1}</td>
                  <td className="p-2 text-sm text-gray-800">{leg.vehicleType}</td>
                  <td className="p-2 text-right text-sm text-gray-800">{formatNumber(leg.distance, 0)} km</td>
                </tr>
              ))}
            </tbody>
            {legs.length > 1 && (
              <tfoot>
                <tr className="border-t border-gray-200 font-medium">
                  <td className="p-2 text-sm text-gray-700" colSpan={2}>
                    {t("results.transport.totalDistance")}
                  </td>
                  <td className="p-2 text-right text-sm text-gray-800">{formatNumber(totalDistance, 0)} km</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
